import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import './Modals.css';
import './LoginModal.css';

const TABS = {
  LOGIN: 'login',
  REQUEST: 'request',
};

const ROLE_OPTIONS = [
  { value: 'gestor_municipal', label: 'Gestor Municipal' },
  { value: 'prestadora', label: 'Prestadora' },
];

function StatusBanner({ tone, children }) {
  if (!children) return null;
  return (
    <div className={`auth-modal__status auth-modal__status--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

function AuthField({ id, label, hint, error, children }) {
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    <div className={`auth-modal__field${error ? ' auth-modal__field--error' : ''}`}>
      <label className="auth-modal__label" htmlFor={id}>{label}</label>
      {children(describedBy)}
      {hint && (
        <p className="auth-modal__hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {error && (
        <p className="auth-modal__field-error" id={`${id}-error`} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function EyeIcon({ visible }) {
  if (visible) {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M10.7 10.7a2 2 0 0 0 2.6 2.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M9.3 5.2A11 11 0 0 1 12 5c5 0 9.3 3.1 11 7a11.8 11.8 0 0 1-4.1 5.1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M6.3 6.3A11.8 11.8 0 0 0 1 12c1.7 3.9 6 7 11 7a11 11 0 0 0 2.7-.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function PasswordInput({
  id,
  value,
  onChange,
  placeholder,
  autoComplete,
  disabled,
  describedBy,
  invalid,
  visible,
  onToggle,
}) {
  return (
    <div className="auth-modal__password-wrap">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        className="auth-modal__input auth-modal__input--password"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={invalid}
      />
      <button
        type="button"
        className="auth-modal__password-toggle"
        onClick={onToggle}
        aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
        aria-pressed={visible}
      >
        <EyeIcon visible={visible} />
      </button>
    </div>
  );
}

function LoginModal({ onClose, initialTab = TABS.LOGIN }) {
  const { login } = useAuth();

  const [activeTab, setActiveTab] = useState(initialTab);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginPasswordVisible, setLoginPasswordVisible] = useState(false);

  const [requestForm, setRequestForm] = useState({
    name: '',
    email: '',
    role: 'gestor_municipal',
    organization: '',
    password: '',
  });
  const [requestErrors, setRequestErrors] = useState({});
  const [requestError, setRequestError] = useState(null);
  const [requestSuccess, setRequestSuccess] = useState(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestPasswordVisible, setRequestPasswordVisible] = useState(false);

  const loginEmailRef = useRef(null);
  const requestNameRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === TABS.LOGIN) {
        loginEmailRef.current?.focus();
      } else {
        requestNameRef.current?.focus();
      }
    }, 40);
    return () => clearTimeout(timer);
  }, [activeTab]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  function switchTab(nextTab) {
    setActiveTab(nextTab);
    if (nextTab === TABS.LOGIN) {
      setLoginError(null);
    } else {
      setRequestError(null);
    }
  }

  function validateRequestForm() {
    const errors = {};

    if (!requestForm.name.trim()) errors.name = 'Informe seu nome completo.';
    if (!requestForm.email.trim()) {
      errors.email = 'Informe seu email.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(requestForm.email)) {
      errors.email = 'Informe um email válido.';
    }
    if (!requestForm.organization.trim()) {
      errors.organization = 'Informe sua instituição ou organização.';
    }
    if (!requestForm.password) {
      errors.password = 'Defina uma senha.';
    } else if (requestForm.password.length < 8) {
      errors.password = 'A senha deve ter pelo menos 8 caracteres.';
    }

    return errors;
  }

  async function handleLoginSubmit(e) {
    e.preventDefault();
    setLoginError(null);
    setLoginLoading(true);

    try {
      await login(loginForm.email, loginForm.password);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Erro ao fazer login. Tente novamente.';
      setLoginError(msg);
    } finally {
      setLoginLoading(false);
    }
  }

  async function handleRequestSubmit(e) {
    e.preventDefault();
    const errors = validateRequestForm();
    setRequestErrors(errors);
    setRequestError(null);
    setRequestSuccess(null);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setRequestLoading(true);
    try {
      const res = await authAPI.requestRegister(requestForm);
      setRequestSuccess(
        res?.data?.message ||
        'Solicitação enviada com sucesso. Sua conta ficará disponível após aprovação administrativa.'
      );
      setRequestForm({
        name: '',
        email: '',
        role: 'gestor_municipal',
        organization: '',
        password: '',
      });
      setRequestErrors({});
      setRequestPasswordVisible(false);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Não foi possível enviar sua solicitação agora.';
      setRequestError(msg);
    } finally {
      setRequestLoading(false);
    }
  }

  function handleLoginChange(field, value) {
    setLoginForm((prev) => ({ ...prev, [field]: value }));
    if (loginError) setLoginError(null);
  }

  function handleRequestChange(field, value) {
    setRequestForm((prev) => ({ ...prev, [field]: value }));
    if (requestErrors[field]) {
      setRequestErrors((prev) => ({ ...prev, [field]: null }));
    }
    if (requestError) setRequestError(null);
    if (requestSuccess) setRequestSuccess(null);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-box auth-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
      >
        <div className="auth-modal__hero">
          <button
            ref={closeBtnRef}
            className="modal-close auth-modal__close"
            onClick={onClose}
            aria-label="Fechar autenticação"
            type="button"
          >
            ×
          </button>

          <div className="auth-modal__brand">
            <div className="auth-modal__brand-copy">
              <h2 className="auth-modal__title" id="auth-modal-title">
                Painel Interativo InfoVivo
              </h2>
            </div>
            <div className="auth-modal__brand-logo-wrap" aria-hidden="true">
              <img
                src="/Logo_2.png"
                alt=""
                className="auth-modal__logo"
                width="176"
                height="88"
              />
            </div>
          </div>
        </div>

        <div className="auth-modal__tabs" role="tablist" aria-label="Fluxos de autenticação">
          <span
            className={`auth-modal__tab-indicator auth-modal__tab-indicator--${activeTab}`}
            aria-hidden="true"
          />
          <button
            type="button"
            id="auth-tab-login"
            role="tab"
            className={`auth-modal__tab${activeTab === TABS.LOGIN ? ' auth-modal__tab--active' : ''}`}
            aria-selected={activeTab === TABS.LOGIN}
            aria-controls="auth-panel-login"
            onClick={() => switchTab(TABS.LOGIN)}
          >
            Login
          </button>
          <button
            type="button"
            id="auth-tab-request"
            role="tab"
            className={`auth-modal__tab${activeTab === TABS.REQUEST ? ' auth-modal__tab--active' : ''}`}
            aria-selected={activeTab === TABS.REQUEST}
            aria-controls="auth-panel-request"
            onClick={() => switchTab(TABS.REQUEST)}
          >
            Solicitação de Registro
          </button>
        </div>

        <div className="modal-body auth-modal__body">
          {activeTab === TABS.LOGIN ? (
            <section
              id="auth-panel-login"
              className="auth-modal__panel auth-modal__panel--login"
              role="tabpanel"
              aria-labelledby="auth-tab-login"
            >
              <div className="auth-modal__panel-header">
                <h3 className="auth-modal__panel-title">Usuários já cadastrados</h3>
                <p className="auth-modal__panel-copy">
                  Use seu email e senha para acessar os recursos do seu perfil.
                </p>
              </div>

              <StatusBanner tone="error">{loginError}</StatusBanner>

              <form className="auth-modal__form" onSubmit={handleLoginSubmit} noValidate>
                <AuthField id="auth-login-email" label="Email">
                  {(describedBy) => (
                    <input
                      ref={loginEmailRef}
                      id="auth-login-email"
                      type="email"
                      className="auth-modal__input"
                      value={loginForm.email}
                      onChange={(e) => handleLoginChange('email', e.target.value)}
                      placeholder="seu@email.com.br"
                      autoComplete="email"
                      disabled={loginLoading}
                      aria-describedby={describedBy}
                    />
                  )}
                </AuthField>

                <AuthField id="auth-login-password" label="Senha">
                  {(describedBy) => (
                    <PasswordInput
                      id="auth-login-password"
                      value={loginForm.password}
                      onChange={(e) => handleLoginChange('password', e.target.value)}
                      placeholder="Digite sua senha"
                      autoComplete="current-password"
                      disabled={loginLoading}
                      describedBy={describedBy}
                      visible={loginPasswordVisible}
                      onToggle={() => setLoginPasswordVisible((prev) => !prev)}
                    />
                  )}
                </AuthField>

                <div className="auth-modal__actions">
                  <button
                    type="submit"
                    className="auth-modal__submit"
                    disabled={loginLoading || !loginForm.email || !loginForm.password}
                  >
                    {loginLoading ? (
                      <>
                        <span className="auth-modal__spinner" aria-hidden="true" />
                        Entrando...
                      </>
                    ) : 'Entrar'}
                  </button>
                </div>
              </form>
            </section>
          ) : (
            <section
              id="auth-panel-request"
              className="auth-modal__panel auth-modal__panel--request"
              role="tabpanel"
              aria-labelledby="auth-tab-request"
            >
              <div className="auth-modal__panel-header">
                <h3 className="auth-modal__panel-title">Solicitar criação de conta</h3>
                <p className="auth-modal__panel-copy">
                  Preencha os dados abaixo para enviar sua solicitação de acesso.
                </p>
              </div>

              <StatusBanner tone="success">{requestSuccess}</StatusBanner>
              <StatusBanner tone="error">{requestError}</StatusBanner>

              <form className="auth-modal__form" onSubmit={handleRequestSubmit} noValidate>
                <div className="auth-modal__grid">
                  <AuthField id="auth-request-name" label="Nome completo" error={requestErrors.name}>
                    {(describedBy) => (
                      <input
                        ref={requestNameRef}
                        id="auth-request-name"
                        type="text"
                        className="auth-modal__input"
                        value={requestForm.name}
                        onChange={(e) => handleRequestChange('name', e.target.value)}
                        placeholder="Seu nome completo"
                        autoComplete="name"
                        disabled={requestLoading}
                        aria-invalid={!!requestErrors.name}
                        aria-describedby={describedBy}
                      />
                    )}
                  </AuthField>

                  <AuthField id="auth-request-email" label="Email institucional" error={requestErrors.email}>
                    {(describedBy) => (
                      <input
                        id="auth-request-email"
                        type="email"
                        className="auth-modal__input"
                        value={requestForm.email}
                        onChange={(e) => handleRequestChange('email', e.target.value)}
                        placeholder="nome@instituicao.gov.br"
                        autoComplete="email"
                        disabled={requestLoading}
                        aria-invalid={!!requestErrors.email}
                        aria-describedby={describedBy}
                      />
                    )}
                  </AuthField>

                  <AuthField id="auth-request-role" label="Perfil solicitado">
                    {(describedBy) => (
                      <select
                        id="auth-request-role"
                        className="auth-modal__input auth-modal__select"
                        value={requestForm.role}
                        onChange={(e) => handleRequestChange('role', e.target.value)}
                        disabled={requestLoading}
                        aria-describedby={describedBy}
                      >
                        {ROLE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </AuthField>

                  <AuthField
                    id="auth-request-organization"
                    label="Instituição / organização"
                    error={requestErrors.organization}
                  >
                    {(describedBy) => (
                      <input
                        id="auth-request-organization"
                        type="text"
                        className="auth-modal__input"
                        value={requestForm.organization}
                        onChange={(e) => handleRequestChange('organization', e.target.value)}
                        placeholder="Prefeitura, autarquia, empresa ou órgão"
                        autoComplete="organization"
                        disabled={requestLoading}
                        aria-invalid={!!requestErrors.organization}
                        aria-describedby={describedBy}
                      />
                    )}
                  </AuthField>

                  <div className="auth-modal__grid-full">
                    <AuthField
                      id="auth-request-password"
                      label="Senha inicial"
                      hint="Mínimo de 8 caracteres. Ela será usada após a aprovação da conta."
                      error={requestErrors.password}
                    >
                      {(describedBy) => (
                        <PasswordInput
                          id="auth-request-password"
                          value={requestForm.password}
                          onChange={(e) => handleRequestChange('password', e.target.value)}
                          placeholder="Crie uma senha segura"
                          autoComplete="new-password"
                          disabled={requestLoading}
                          describedBy={describedBy}
                          invalid={!!requestErrors.password}
                          visible={requestPasswordVisible}
                          onToggle={() => setRequestPasswordVisible((prev) => !prev)}
                        />
                      )}
                    </AuthField>
                  </div>
                </div>

                <div className="auth-modal__actions">
                  <button
                    type="submit"
                    className="auth-modal__submit"
                    disabled={requestLoading}
                  >
                    {requestLoading ? (
                      <>
                        <span className="auth-modal__spinner" aria-hidden="true" />
                        Enviando solicitação...
                      </>
                    ) : 'Enviar solicitação'}
                  </button>
                </div>
              </form>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginModal;
